import React, { useId } from "react";
import { Box } from "@mui/material";
import {
  RANKING_PODCAST_BADGE_GAP_PX,
  RANKING_PODCAST_BADGE_OVERFLOW_PX,
  RANKING_PODCAST_BADGE_RADIUS_PX,
  RANKING_PODCAST_BADGE_SIZE_PX,
  RANKING_THUMB_PX,
  rankingThumbRadius,
} from "./rankingAssetDimensions";

/** Extra paint so the hanging badge is not flush with the clip edge. */
const VIEW_PAD_PX = 1;

/** Shared layout: badge box + evenly expanded cutout (integer px, one coordinate space). */
export function getPodcastBadgeLayout(thumbPx: number = RANKING_THUMB_PX) {
  const scale = thumbPx / RANKING_THUMB_PX;
  const size = RANKING_PODCAST_BADGE_SIZE_PX * scale;
  const overflow = RANKING_PODCAST_BADGE_OVERFLOW_PX * scale;
  const gap = RANKING_PODCAST_BADGE_GAP_PX * scale;
  const pad = VIEW_PAD_PX * scale;
  const badge = {
    x: -overflow,
    y: thumbPx - size + overflow,
    size,
    rx: RANKING_PODCAST_BADGE_RADIUS_PX * scale,
  };
  const view = {
    x: badge.x - pad,
    y: 0,
    w: thumbPx - (badge.x - pad),
    h: Math.max(thumbPx, badge.y + badge.size + pad),
  };
  return {
    thumb: thumbPx,
    badge,
    cutout: {
      x: badge.x - gap,
      y: badge.y - gap,
      size: badge.size + gap * 2,
      rx: badge.rx + gap,
    },
    /** Paint box including the left/bottom badge hang (viewBox 0,0,thumb clips it). */
    view,
    /** Positions inside `view` for PNG capture (no negative coordinates). */
    capture: {
      cover: { x: -view.x, y: view.y, size: thumbPx },
      badge: {
        x: badge.x - view.x,
        y: badge.y - view.y,
      },
    },
  };
}

export function xhtmlImg(
  src: string,
  width: number,
  height: number,
  extras: Record<string, string> = {},
  borderRadius?: number,
) {
  return React.createElement(
    "div",
    {
      xmlns: "http://www.w3.org/1999/xhtml",
      style: {
        width,
        height,
        margin: 0,
        padding: 0,
        overflow: "hidden",
        borderRadius: borderRadius ?? 0,
        backgroundColor: "rgba(33,35,45,0.85)",
      },
    },
    React.createElement("img", {
      src,
      alt: "",
      ...extras,
      style: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
      },
    }),
  );
}

/**
 * Cover + channel badge in one overflowing SVG so the gap is aligned in
 * the same user space (HTML-sibling badge was drifting vs the mask hole).
 *
 * Internal SVG user space scales with `sizePx`; use `contained` when the
 * parent is sized to `getPodcastBadgeLayout(sizePx).view` (livestream rows).
 */
export function ProgramThumbWithPodcastBadge({
  coverUrl,
  podcastImageUrl,
  maskId,
  sizePx = RANKING_THUMB_PX,
  /** When true, parent is sized to `view` and SVG anchors at 0,0 (livestream table rows). */
  contained = false,
}: {
  coverUrl: string;
  podcastImageUrl: string;
  maskId: string;
  /** Rendered CSS size; defaults to the 100px design thumb. */
  sizePx?: number;
  contained?: boolean;
}) {
  const { thumb, badge, cutout, view } = getPodcastBadgeLayout(sizePx);
  const thumbRadius = rankingThumbRadius(sizePx);
  const uid = useId().replace(/:/g, "");
  const clipId = `${maskId}-${uid}-clip`;
  const resolvedMaskId = `${maskId}-${uid}`;
  return (
    <Box
      component="svg"
      data-ranking-program-thumb-svg=""
      data-thumb-px={sizePx}
      data-contained={contained ? "" : undefined}
      // html2canvas clones often drop foreignObject <img>s — export reads these.
      data-cover-src={coverUrl}
      data-badge-src={podcastImageUrl}
      width={view.w}
      height={view.h}
      viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
      overflow="hidden"
      sx={{
        display: "block",
        width: view.w,
        height: view.h,
        overflow: "hidden",
        position: "absolute",
        left: contained ? 0 : view.x,
        top: contained ? 0 : view.y,
      }}
    >
      <defs>
        <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
          <rect width={thumb} height={thumb} rx={thumbRadius} ry={thumbRadius} />
        </clipPath>
        <mask id={resolvedMaskId} maskUnits="userSpaceOnUse">
          <rect width={thumb} height={thumb} fill="white" />
          <rect
            x={cutout.x}
            y={cutout.y}
            width={cutout.size}
            height={cutout.size}
            rx={cutout.rx}
            ry={cutout.rx}
            fill="black"
          />
        </mask>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <foreignObject
          width={thumb}
          height={thumb}
          mask={`url(#${resolvedMaskId})`}
        >
          {xhtmlImg(coverUrl, thumb, thumb, {
            "data-ranking-cover-cutout": "",
          })}
        </foreignObject>
      </g>
      <foreignObject
        x={badge.x}
        y={badge.y}
        width={badge.size}
        height={badge.size}
      >
        {xhtmlImg(
          podcastImageUrl,
          badge.size,
          badge.size,
          { "data-ranking-podcast-badge": "" },
          badge.rx,
        )}
      </foreignObject>
    </Box>
  );
}
